(() => {
  'use strict';

  // Contract with snippets/kns-price-match.liquid: [data-kns-price-match-root]
  // with data-endpoint, data-available ("true"/"false"/"" from the rendered
  // variant), optional data-clearance="1" when a product tag rules it out.
  // Sold-out is read client-side from the SAME availability signal as
  // kns-notify — the buy buttons' [name="add"] disabled state — so a sold-out
  // variant hides the block and the back-in-stock form covers it instead.
  const STATE_CLASSES = {
    submitting: 'kns-price-match--submitting',
    success: 'kns-price-match--success',
    error: 'kns-price-match--error',
  };

  const MESSAGES = {
    genericInvalid: 'Please check the highlighted fields.',
    throttled: "You've sent a few requests today — we'll be in touch soon.",
    network: "Sorry, that didn't send. Please try again or email us.",
    photoInvalid: 'Please attach a JPG, PNG or WebP under 5 MB.',
    photoOk: "We've got your screenshot too.",
    photoFail: "Your request is in, but the screenshot didn't upload — you can reply to our email with it.",
    photoUploading: 'Uploading your screenshot…',
  };

  // The ONLY place a customer's screenshot may go (review: a tampered or misconfigured
  // response must never send it to another host, or over plain http).
  const PHOTO_UPLOAD_URL = 'https://marketing.knivesandstones.com.au/apps/kns/price-match/photo';

  // Client mirror of the upload endpoint's limits (it stays the source of truth):
  // the same types the file input accepts, the same 5 MB the hint promises.
  const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
  const PHOTO_TIMEOUT_MS = 30000;
  const PHOTO_ERROR_MAX_CHARS = 140;

  const OFF_KEY = 'kns-price-match-off';
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const httpsPattern = /^https:\/\/\S+$/i;
  const postcodePattern = /^\d{4}$/;

  const FIELD_KEYS = ['competitor_url', 'price_seen', 'email', 'postcode', 'country', 'note'];

  function sessionOff(value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(OFF_KEY) === '1';
      window.sessionStorage.setItem(OFF_KEY, value ? '1' : '');
    } catch {
      // private mode / disabled storage: behave as "not off"
    }
    return false;
  }

  function buyForm(root) {
    // kns-notify's lookup first; a bare form only as a last resort.
    return root.parentElement?.querySelector('product-form form')
      ?? root.parentElement?.querySelector('form:not(.kns-price-match__form)')
      ?? null;
  }

  function selectedVariantId(root) {
    const form = buyForm(root);
    const id = Number.parseInt(form?.querySelector('[name="id"]')?.value, 10);
    // Omit rather than send null/NaN when the input is missing or blank; the
    // backend's 422 for variant_id then surfaces at the top of the form.
    return Number.isInteger(id) && id > 0 ? id : undefined;
  }

  function addButton(form) {
    return form?.querySelector('[name="add"]') ?? null;
  }

  function elements(root) {
    const form = root.querySelector('[data-kns-price-match-form]');
    const inputs = {};
    const errors = {};
    for (const key of FIELD_KEYS) {
      inputs[key] = form.querySelector(`[name="${key}"]`);
      errors[key] = root.querySelector(`#${root.id}-${key}-error`);
    }
    return {
      // The photo input is looked up under its own key, never a FIELD_KEYS one:
      // the FIELD_KEYS loop above reads it into neither `inputs` nor `errors`,
      // so clientChecks/send can't see it, and clearErrors/showFieldError only
      // touch it through the explicit 'photo' calls below.
      photoInput: form.querySelector('[data-kns-price-match-photo]'),
      photoError: root.querySelector(`#${root.id}-photo-error`),
      toggle: root.querySelector('[data-kns-price-match-toggle]'),
      panel: root.querySelector('[data-kns-price-match-panel]'),
      form,
      inputs,
      errors,
      topError: root.querySelector('[data-kns-price-match-top-error]'),
      message: root.querySelector('[data-kns-price-match-message]'),
      submit: root.querySelector('[data-kns-price-match-submit]'),
    };
  }

  function openPanel(root, els) {
    root.classList.add('kns-price-match--open');
    els.toggle.setAttribute('aria-expanded', 'true');
    els.panel.hidden = false;
    els.form.querySelector('input, select, textarea')?.focus();
  }

  function closePanel(root, els) {
    root.classList.remove('kns-price-match--open');
    els.toggle.setAttribute('aria-expanded', 'false');
    els.panel.hidden = true;
  }

  function clearErrors(root, els) {
    for (const key of FIELD_KEYS) {
      const error = els.errors[key];
      if (!error) continue;
      error.hidden = true;
      error.textContent = '';
      els.inputs[key]?.setAttribute('aria-invalid', 'false');
    }
    els.topError.hidden = true;
    els.topError.textContent = '';
    if (els.photoError) {
      els.photoError.hidden = true;
      els.photoError.textContent = '';
      els.photoInput?.setAttribute('aria-invalid', 'false');
    }
    root.classList.remove(STATE_CLASSES.error);
  }

  function showFieldError(els, key, text) {
    // 'photo' is not a FIELD_KEYS field, so it keeps its own two elements rather
    // than entries in `errors`/`inputs`.
    if (key === 'photo') {
      if (!els.photoError) return;
      els.photoError.textContent = text;
      els.photoError.hidden = false;
      els.photoInput?.setAttribute('aria-invalid', 'true');
      return;
    }
    const error = els.errors[key];
    if (!error) return;
    error.textContent = text;
    error.hidden = false;
    els.inputs[key]?.setAttribute('aria-invalid', 'true');
  }

  function showMessage(root, els, text, isError) {
    els.message.textContent = text;
    els.message.hidden = false;
    root.classList.toggle(STATE_CLASSES.error, Boolean(isError));
  }

  function clientChecks(els) {
    // Mirrors the server rules; the server stays the source of truth, so a
    // passing client check still posts and the 422 is rendered verbatim.
    const problems = [];
    if (!httpsPattern.test(els.inputs.competitor_url.value.trim())) problems.push('competitor_url');
    if (!emailPattern.test(els.inputs.email.value.trim())) problems.push('email');
    // Location is optional (James 6 Oct 2026); only an Australian postcode has a format to check.
    const postcode = els.inputs.postcode.value.trim();
    const inAustralia = /^(australia|au)$/i.test(els.inputs.country.value.trim());
    if (postcode && inAustralia && !postcodePattern.test(postcode)) problems.push('postcode');
    return problems;
  }

  function syncAvailability(root, els) {
    if (root.dataset.knsPriceMatchTagHidden === '1') return;
    if (sessionOff(undefined)) { root.hidden = true; return; }
    const button = addButton(buyForm(root));
    if (!button) return;                       // no signal: trust the static render
    if (root.hidden !== button.disabled) root.hidden = button.disabled;
  }

  function chosenPhoto(els) {
    return els.photoInput?.files?.[0] ?? null;
  }

  function photoUsable(upload) {
    // Absent (backend feature off) or malformed → no upload at all, exactly the
    // pre-photo behaviour. max_bytes is not read: the client check is the 5 MB
    // the hint promises, and a smaller server cap fails cleanly into photoFail.
    return Boolean(upload)
      && upload.url === PHOTO_UPLOAD_URL
      && typeof upload.token === 'string' && upload.token !== '';
  }

  function photoErrorText(photoUpload) {
    // The sentence added to the success message when the upload fails. A server
    // sentence is used only when it is one plain line of sensible length;
    // anything else, including nothing at all, gets the fixed copy.
    const text = typeof photoUpload?.error === 'string' ? photoUpload.error.trim() : '';
    const plain = text !== '' && text.length <= PHOTO_ERROR_MAX_CHARS
      && !/[<>]/.test(text) && !/[\u0000-\u001f\u007f]/.test(text);
    return plain ? text : MESSAGES.photoFail;
  }

  async function uploadPhoto(upload, file) {
    // One try, no retries, no polling: the request is already in, so a photo
    // timeout, a 4xx or a body that isn't {"status":"received"} is only a sentence.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PHOTO_TIMEOUT_MS);
    try {
      const body = new FormData();
      body.append('token', upload.token);
      body.append('file', file);
      const response = await fetch(upload.url, {
        method: 'POST',
        body,
        mode: 'cors',
        credentials: 'omit',
        signal: controller.signal,
      });
      if (!response.ok) return { ok: false, error: (await response.json().catch(() => null))?.error };
      const payload = await response.json().catch(() => null);
      return payload?.status === 'received'
        ? { ok: true }
        : { ok: false, error: payload?.error };
    } catch {
      return { ok: false, error: undefined };   // timeout / network / CORS
    } finally {
      clearTimeout(timer);
    }
  }

  async function photoSentence(els, payload) {
    // The request is already in and counted as a success; the photo can only
    // add ONE sentence to it, never take the success away. No photo_upload in
    // the response (the feature is dark) means the message is exactly today's.
    const file = chosenPhoto(els);
    if (!file || !photoUsable(payload.photo_upload)) return payload.message;
    const attempt = await uploadPhoto(payload.photo_upload, file);
    if (attempt.ok) return `${payload.message} ${MESSAGES.photoOk}`;
    return `${payload.message} ${photoErrorText(attempt)}`;
  }

  async function send(root, els) {
    // The double-submit guard is the per-root `sending` flag in the submit
    // handler — requestSubmit()/Enter bypass a disabled button, so the button
    // is deliberately not disabled here.
    const body = {
      competitor_url: els.inputs.competitor_url.value.trim(),
      email: els.inputs.email.value.trim(),
    };
    const postcode = els.inputs.postcode.value.trim();
    if (postcode) body.postcode = postcode;
    const country = els.inputs.country.value.trim();
    if (country) body.country = country;
    const variantId = selectedVariantId(root);
    if (variantId !== undefined) body.variant_id = variantId;
    const priceSeen = Number.parseFloat(els.inputs.price_seen.value.replace(/,/g, ''));
    if (els.inputs.price_seen.value.trim() !== '' && Number.isFinite(priceSeen)) body.price_seen = priceSeen;
    const note = els.inputs.note.value.trim();
    if (note) body.note = note.slice(0, 500);

    Object.values(STATE_CLASSES).forEach((className) => root.classList.remove(className));
    root.classList.add(STATE_CLASSES.submitting);

    try {
      const response = await fetch(root.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.status === 404) {
        // Feature is off server-side — stop rendering the block for this session.
        sessionOff(true);
        closePanel(root, els);               // no lingering open class / aria-expanded
        root.hidden = true;
        return;
      }

      clearErrors(root, els);
      let payload = null;
      try { payload = await response.json(); } catch { payload = null; }

      if (response.ok && payload && typeof payload.message === 'string') {
        // 200 received / not_eligible: the backend's message verbatim, in a
        // live region, replacing the form.
        root.classList.add(STATE_CLASSES.success);
        els.form.hidden = true;
        // The base success shows at once (review nit: a slow upload left a blank
        // panel); the photo outcome replaces it when the upload settles.
        const uploading = chosenPhoto(els) && photoUsable(payload.photo_upload);
        showMessage(root, els, uploading ? `${payload.message} ${MESSAGES.photoUploading}` : payload.message, false);
        if (uploading) showMessage(root, els, await photoSentence(els, payload), false);
      } else if (response.status === 422 && payload) {
        root.classList.add(STATE_CLASSES.error);
        const errors = payload.errors;
        if (errors && typeof errors === 'object') {
          for (const key of Object.keys(errors)) {
            const text = String(errors[key] ?? 'Please check this field.');
            if (els.inputs[key]) showFieldError(els, key, text);
            else { // variant_id and any unknown key have no field — top of the form
              els.topError.textContent = els.topError.textContent ? `${els.topError.textContent} ${text}` : text;
              els.topError.hidden = false;
            }
          }
        } else if (typeof payload.message === 'string') {
          els.topError.textContent = payload.message;
          els.topError.hidden = false;
        }
      } else if (response.status === 429) {
        root.classList.add(STATE_CLASSES.error);
        showMessage(root, els, MESSAGES.throttled, true);
      } else {
        // 413, 401, 5xx, anything unexpected: generic line, the form stays.
        root.classList.add(STATE_CLASSES.error);
        showMessage(root, els, MESSAGES.network, true);
      }
    } catch {
      root.classList.add(STATE_CLASSES.error);
      showMessage(root, els, MESSAGES.network, true);
    } finally {
      root.classList.remove(STATE_CLASSES.submitting);
    }
  }

  function initialise(root) {
    // The tag verdict wins even on re-initialise (a stale attribute never
    // reopens the block), so it is applied before the ready guard.
    if (root.dataset.clearance === '1') {
      root.hidden = true;
      root.dataset.knsPriceMatchTagHidden = '1';
    } else if (sessionOff(undefined)) {
      root.hidden = true;
    }

    if (root.dataset.knsPriceMatchReady === 'true') return root;
    root.dataset.knsPriceMatchReady = 'true';

    const els = elements(root);
    if (!els.toggle || !els.panel || !els.form) return root;
    if (root.dataset.knsPriceMatchTagHidden === '1' || sessionOff(undefined)) return root; // bind nothing on a block nobody sees

    // Static render signal (product.available); the client-side [name=add]
    // check below keeps it honest across variant switches.
    if (root.dataset.available === 'false') root.hidden = true;

    els.toggle.addEventListener('click', () => {
      if (els.toggle.getAttribute('aria-expanded') === 'true') closePanel(root, els);
      else openPanel(root, els);
    });

    // Escape inside the panel (or on the toggle) closes it and returns focus,
    // as the toggle click does.
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && els.toggle.getAttribute('aria-expanded') === 'true') {
        closePanel(root, els);
        els.toggle.focus();
      }
    };
    els.panel.addEventListener('keydown', onKeyDown);
    els.toggle.addEventListener('keydown', onKeyDown);

    let sending = false;   // per-root in-flight guard; requestSubmit/Enter bypass a disabled button
    els.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (sending) return; // a send is in flight
      sending = true;
      try {
        els.message.hidden = true;
        clearErrors(root, els);
        // The photo goes wrong on its own (a file the endpoint would reject),
        // not mixed into the generic line, so the fix is obvious — and it is
        // checked BEFORE the field is locked, since focus is a no-op on a
        // disabled input. Before the POST too: a wrong file never reaches the
        // wire, where it would only answer 4xx behind the generic network line.
        const photo = chosenPhoto(els);
        if (photo && (!PHOTO_TYPES.includes(photo.type) || photo.size > PHOTO_MAX_BYTES)) {
          showFieldError(els, 'photo', MESSAGES.photoInvalid);
          els.photoInput?.focus();
          return;
        }
        // Locked for the whole submit + any upload, like kns-notify's email
        // field; the finally below always releases it.
        if (els.photoInput) els.photoInput.disabled = true;
        const problems = clientChecks(els);
        if (problems.length) {
          showMessage(root, els, MESSAGES.genericInvalid, true);
          els.inputs[problems[0]]?.focus();
          return;
        }
        await send(root, els);
      } finally {
        // Guarded, and BEFORE sending is released: an unguarded throw here (a
        // render without the field) would strand `sending` true and wedge the
        // form for the session.
        if (els.photoInput) els.photoInput.disabled = false;   // locked while the POST + any upload run
        sending = false;
      }
    });

    // Same wiring kns-notify uses for variant switches: the buy-buttons block
    // and the variant picker are sibling subtrees, so bind on a true common
    // ancestor for the section event, plus a bubble 'change' for plain inputs.
    const host = root.parentElement?.closest('.product, shopify-section, section') ?? root.parentElement;
    host?.addEventListener('on:variant:change', () => syncAvailability(root, els));
    root.parentElement?.addEventListener('change', () => syncAvailability(root, els));
    syncAvailability(root, els);

    return root;
  }

  function initAll() {
    document.querySelectorAll('[data-kns-price-match-root]').forEach(initialise);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  window.KnsPriceMatch = { initialise, syncAvailability, MESSAGES };
})();
